import React, { useCallback, useEffect, useRef } from "react";
import { FiArrowUp, FiSquare } from "react-icons/fi";

const MAX_HEIGHT = 208; // ~8 rows before the textarea scrolls internally

/**
 * The message composer.
 *
 * Differences from the old input that matter for the feel:
 *  - grows with the content instead of being locked to two rows
 *  - the send button reflects whether there is anything to send
 *  - turns into a stop button while an answer is generating
 *  - the focus ring lives on the whole container, so removing the textarea's
 *    own outline no longer leaves keyboard users with no focus indicator
 *  - settings sit underneath in a quiet footer rather than above in 140px of
 *    chrome
 */
export default function Composer({
  value,
  onChange,
  onSubmit,
  onStop,
  isBusy = false,
  canStop = false,
  placeholder = "Ask anything about TNS payments or connectivity",
  autoFocus = false,
  footer = null,
}) {
  const textareaRef = useRef(null);

  const resize = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    const next = Math.min(el.scrollHeight, MAX_HEIGHT);
    el.style.height = `${next}px`;
    el.style.overflowY = el.scrollHeight > MAX_HEIGHT ? "auto" : "hidden";
  }, []);

  useEffect(resize, [value, resize]);

  useEffect(() => {
    if (autoFocus) textareaRef.current?.focus();
  }, [autoFocus]);

  // Return focus to the composer once a response finishes, so the next
  // question can be typed without reaching for the mouse. Guarded on the
  // busy -> idle transition specifically: focusing on mount would steal the
  // caret on the pages that embed this below other form controls.
  const wasBusy = useRef(false);
  useEffect(() => {
    if (wasBusy.current && !isBusy) textareaRef.current?.focus();
    wasBusy.current = isBusy;
  }, [isBusy]);

  const submit = () => {
    if (isBusy || !value.trim()) return;
    onSubmit?.(value.trim());
  };

  const handleKeyDown = (event) => {
    // isComposing guards IME input: committing a Japanese or Chinese
    // candidate with Enter must not send the message.
    if (
      event.key === "Enter" &&
      !event.shiftKey &&
      !event.nativeEvent?.isComposing
    ) {
      event.preventDefault();
      submit();
    }
  };

  const hasText = Boolean(value.trim());

  return (
    <div className="rounded-[22px] border border-iq-line bg-white shadow-sm transition-colors focus-within:border-iq-brand/50 focus-within:ring-2 focus-within:ring-iq-brand/15">
      <div className="flex items-end gap-2 px-3.5 pt-3 pb-2">
        <label htmlFor="iq-composer" className="sr-only">
          Ask a question about TNS payments or connectivity
        </label>
        <textarea
          id="iq-composer"
          ref={textareaRef}
          rows={1}
          value={value}
          onChange={(event) => onChange?.(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={isBusy && !canStop}
          className="iq-scroll max-h-52 flex-1 resize-none border-0 bg-transparent py-1.5 text-[15px] leading-[1.6] text-iq-ink placeholder-iq-ink-faint focus:outline-none focus:ring-0 disabled:opacity-60"
        />

        {canStop ? (
          <button
            type="button"
            onClick={onStop}
            aria-label="Stop generating"
            className="mb-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-iq-ink text-white transition-colors hover:bg-iq-ink/85 focus:outline-none focus-visible:ring-2 focus-visible:ring-iq-brand focus-visible:ring-offset-2"
          >
            <FiSquare className="h-3 w-3 fill-current" />
          </button>
        ) : (
          <button
            type="button"
            onClick={submit}
            disabled={!hasText || isBusy}
            aria-label="Send question"
            className="mb-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-iq-brand text-white transition-all hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-iq-brand focus-visible:ring-offset-2 disabled:bg-iq-line disabled:text-iq-ink-faint"
          >
            <FiArrowUp className="h-4 w-4" />
          </button>
        )}
      </div>

      {footer && (
        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-iq-line/70 px-2.5 py-1.5">
          {footer}
        </div>
      )}
    </div>
  );
}
